import Vue from 'vue';
import VueRouter from 'vue-router';
import Vuex from 'vuex';
import camelCase from 'lodash/camelCase';
import store from '../store';
import Sittercity from './sittercity';
import { setupApollo } from '../lib/apollo_setup';
import { sendSegmentPageview } from '../lib/segment';
import setupFooter from '@/lib/setup_footer';
import { readDomainCookie } from '@/lib/domain_cookie';

import SitterOnboarding from '../apps/sitter/SitterOnboarding.vue';
import SitterCreateForm from '../apps/sitter/signup/account/SitterCreateForm.vue';
import SitterAddress from '../apps/sitter/signup/address/SitterAddress.vue';
import SitterCareType from '../apps/sitter/signup/careType/SitterCareType.vue';
import HousekeepingForm from '../apps/sitter/signup/profile/HousekeepingForm.vue';
import PetCareForm from '../apps/sitter/signup/profile/PetCareForm.vue';
import CompanionCareForm from '../apps/sitter/signup/profile/CompanionCareForm.vue';

import ProfileIntro from '../apps/sitter/signup/profile/ProfileIntro.vue';
import ProfileSteps from '../apps/sitter/signup/profile/ProfileSteps.vue';
import ProfileStepsComplete from '../apps/sitter/signup/profile/ProfileStepsComplete.vue';

import { redirectIfNotOnboarded, redirectIfOnboarded } from '../lib/sitterOnboarding/sitterOnboardingState';
import { redirect } from '@/helpers/redirects';
import { FETCH_ACCOUNT_DATA } from '@/graphql/account/queries';
import { rolesArray } from '@/lib/jwt';
import { Account, AccountData } from '@/models/account';

// Add helper function for setting care type from URL params
function setCareTypeFromUrlOrStore() {
  try {
    // Check if careType is passed in URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const careTypeParam = urlParams.get('careType');

    // Get care type from Vuex store, if available
    const vuexData = JSON.parse(sessionStorage.getItem('vuex') || '{}');
    const storeCareType = vuexData?.sitterOnboarding?.careType;

    // Use URL parameter if available, otherwise use store value
    const careType = careTypeParam || storeCareType;

    // If we have a careType in the URL but not in the store, set it in the store
    if (careTypeParam && store && store.dispatch) {
      // Use dispatch instead of commit, and convert to camelCase like in SitterCareType
      store.dispatch('setCareType', camelCase(careTypeParam));
    }

    // Check if this is an additional profile creation
    const isAdditionalProfile = readDomainCookie('additional_profile') === 'true';

    return { careType, isAdditionalProfile };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error setting care type:', error);
    return { careType: null, isAdditionalProfile: false };
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (document.getElementById('app') == null) {
    throw new Error('App node not found. Aborting.');
  }

  setupFooter();

  Vue.use(Vuex);

  const apolloProvider = setupApollo(true);

  // Fetch our account details from graphql
  let accountData;
  let account;

  try {
    const result = await apolloProvider.defaultClient.query({
      query: FETCH_ACCOUNT_DATA,
    });
    accountData = result.data.viewer;
  } catch (error) {
    if (window.location.pathname !== '/sitter/signup/account') {
      console.error('Error fetching account data:', error); // eslint-disable-line no-console
    }
  }
  if (accountData) {
    account = new Account({
      ssoUUID: accountData.id,
      id: accountData.id,
      firstName: accountData.firstName,
      lastName: accountData.lastName,
      email: accountData.email,
      originalPhoto: accountData.photoUrl,
      croppedPhoto: accountData.photoUrl,
      location: {
        latitude: parseFloat(accountData.location.geoPoint.y),
        longitude: parseFloat(accountData.location.geoPoint.x),
        zipcode: accountData.location.zipcode,
      },
      hasPremiumHistory: accountData.hasPremiumHistory,
      profilePhotoStatus: accountData.profilePhotoStatus,
      roles: rolesArray(),
    } as AccountData);
  }

  if (account?.isParent()) redirect('/account/seekers/dashboard');

  const routes = [
    {
      component: SitterCreateForm,
      meta: { segmentName: 'Sitter Create Form', requiresOnboardingCheck: true },
      name: 'SitterCreateForm',
      path: '/account',
    },
    {
      component: SitterAddress,
      meta: { segmentName: 'Sitter Address' },
      name: 'address',
      path: '/address',
    },
    {
      component: ProfileIntro,
      name: 'ProfileIntro',
      path: '/profile/intro',
    },
    {
      component: SitterCareType,
      meta: { segmentName: 'Care Type' },
      name: 'care-type',
      path: '/care-type',
      beforeEnter(to, from, next) {
        const { careType, isAdditionalProfile } = setCareTypeFromUrlOrStore();

        if (isAdditionalProfile && careType) {
          const isChildCare = ['babysitter', 'nanny'].includes(careType);
          if (isChildCare) {
            return next('/profile/steps');
          }
          const typeToPathMap = {
            pet_care: '/profile/pet-care',
            companion_care: '/profile/companion-care',
            housekeeping: '/profile/housekeeping',
          };
          return next(typeToPathMap[careType] || '/care-type');
        }

        return next();
      },
    },
    {
      component: ProfileSteps,
      name: 'ProfileSteps',
      path: '/profile/steps',
      beforeEnter(to, from, next) {
        const { careType } = setCareTypeFromUrlOrStore();
        const isChildCare = ['babysitter', 'nanny'].includes(careType);

        if (!careType || !isChildCare) {
          return next('/care-type');
        }

        return next();
      },
    },
    {
      component: HousekeepingForm,
      meta: { segmentName: 'Housekeeping' },
      name: 'housekeeping',
      path: '/profile/housekeeping',
      beforeEnter(to, from, next) {
        // Always set the care type to housekeeping for this form using dispatch
        store.dispatch('setCareType', 'housekeeping');
        return next();
      },
    },
    {
      component: PetCareForm,
      meta: { segmentName: 'Pet' },
      name: 'petcare',
      path: '/profile/pet-care',
      beforeEnter(to, from, next) {
        // Always set the care type to petCare for this form using dispatch
        store.dispatch('setCareType', 'petCare');
        return next();
      },
    },
    {
      component: CompanionCareForm,
      meta: { segmentName: 'Companion' },
      name: 'companion',
      path: '/profile/companion-care',
      beforeEnter(to, from, next) {
        // Always set the care type to companionCare for this form using dispatch
        store.dispatch('setCareType', 'companionCare');

        return next();
      },
    },
    {
      component: ProfileStepsComplete,
      name: 'ProfileStepsComplete',
      path: '/profile/complete',
      props: () => {
        // Check cookie for additional profile
        const isAdditionalProfile = readDomainCookie('additional_profile') === 'true';

        return {
          account,
          isAdditionalProfile,
        };
      },
    },
  ];

  Vue.use(VueRouter);
  const router = new VueRouter({
    mode: 'history',
    routes,
    base: '/sitter/signup',
    scrollBehavior() {
      return { x: 0, y: 0 };
    },
  });

  router.beforeEach(async (to, from, next) => {
    if (to.meta?.requiresOnboardingCheck) {
      await redirectIfNotOnboarded(apolloProvider, account);
    } else {
      await redirectIfOnboarded(apolloProvider, account);
    }

    next();
  });

  router.afterEach((to) => {
    const pageName = to.meta?.segmentName || to.name;
    if (pageName) {
      sendSegmentPageview(`Sitter Signup - ${pageName}`, {
        url: document.location.href,
        path: document.location.pathname,
      });
    }
  });

  new Sittercity({
    name: 'Sitter Onboarding',
    render(createElement) {
      return createElement(SitterOnboarding);
    },
    router,
    apolloProvider,
    store,
  }).$mount('#app');
});
